import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Avatar, Badge, Card, SectionTitle } from '../../components/ui';
import { Icon } from '../../components/icons';
import { BRAND, getThemeColors, DENSITY_VALUES } from '../../lib/theme';
import { v1Dashboard, v1Admins, type V1Admin, type V1Care } from '../../lib/v1';
import {
  v2Matching, sortByAvailability, formatStartdatum,
  type MatchingBoard, type MatchingBoardUser, type MatchCandidate, type MatchField,
} from '../../lib/matching';
import { CareParamIconRow } from '../../components/care/CareParamIcons';
import { WunschIcons, WUNSCH_ICON_KEYS } from '../../components/matching/WunschIcons';
import { MatchScoreBadge } from '../../components/matching/MatchScoreBadge';
import { MatchDetailsPopover } from '../../components/matching/MatchDetailsPopover';
import { HARDCODED } from '../pflege/profile/options';
import { navigateGlobal } from '../../lib/route';
import { AppLink } from '../../lib/openLink';
import { formatDate } from '../../lib/dateFormat';
import { setCareCreateHandoff } from '../../lib/careCreateHandoff';
import type { Density } from '../../types';

/** BK-Kategorie (users.badge: PB = Wunsch, BK = eigene) → Label; „0“/leer = keine Angabe. */
function badgeLabel(v: string | number | null | undefined): string | null {
  if (v == null || v === '' || String(v) === '0') return null;
  return HARDCODED.badge.find(o => o.value === String(v))?.label ?? String(v);
}

/**
 * Matching-Board (Vorlage "Kacheln und Icons CRM 2.0"):
 *
 * Zwei Spalten — links die Pflegeempfänger, rechts die verfügbaren
 * Betreuungskräfte, beide als Karten mit den Pflegeparameter-Icons.
 *
 * Links erscheinen automatisch alle PBs, die die LETZTE Stage erreicht
 * haben ("Aktiver Kunde" — Abschluss der Anfrage). Rechts stehen die BKs
 * immer nach Startdatum („Verfügbar ab", früheste zuerst; pausierende BKs
 * mit ihrem späteren Datum). Klick auf einen PB lädt den Score je BK (bei
 * gleichem Startdatum entscheidet der Score); die
 * Pflegeparameter-Icons, die zwischen PB und BK übereinstimmen, werden
 * hervorgehoben, die übrigen gedimmt.
 */
interface Props {
  dark: boolean;
  density: Density;
}

export function MatchingPage({ dark, density }: Props) {
  const t = getThemeColors(dark);
  const { t: tr } = useTranslation();
  void DENSITY_VALUES[density]; // density aktuell ohne Layout-Einfluss

  const [board, setBoard] = useState<MatchingBoard | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [admins, setAdmins] = useState<V1Admin[]>([]);
  const [busyBkIds, setBusyBkIds] = useState<Set<number>>(new Set());

  // Auswahl links + Kandidaten rechts
  const [selectedPb, setSelectedPb] = useState<MatchingBoardUser | null>(null);
  const [candidates, setCandidates] = useState<MatchCandidate[] | null>(null);
  const [matchLoading, setMatchLoading] = useState(false);
  // BK-ID, deren Score-Aufschlüsselung (inkl. Gewichte) offen ist.
  const [openBreakdown, setOpenBreakdown] = useState<number | null>(null);

  // Spalten-Suchfilter
  const [pbSearch, setPbSearch] = useState('');
  const [bkSearch, setBkSearch] = useState('');

  useEffect(() => {
    let alive = true;
    setLoading(true);
    Promise.allSettled([
      v2Matching.board(),
      v1Dashboard.currentCares({ perPage: 200 }),
      v1Admins.list(),
    ]).then(([boardRes, caresRes, adminsRes]) => {
      if (!alive) return;
      if (boardRes.status === 'fulfilled') setBoard(boardRes.value);
      else setError(tr('matching.board.load_error'));
      if (caresRes.status === 'fulfilled') {
        setBusyBkIds(new Set(caresRes.value.data.map((c: V1Care) => Number(c.bk_user_id)).filter(Boolean)));
      }
      if (adminsRes.status === 'fulfilled') setAdmins(adminsRes.value);
      setLoading(false);
    });
    return () => { alive = false; };
  }, []);

  // PB angeklickt → Kandidaten (BKs) nach Score sortiert laden.
  useEffect(() => {
    setOpenBreakdown(null);
    if (!selectedPb) { setCandidates(null); return; }
    let alive = true;
    setMatchLoading(true);
    v2Matching.candidatesFor(selectedPb.id, 200)
      .then(res => { if (alive) setCandidates(res.candidates); })
      .catch(() => { if (alive) setCandidates([]); })
      .finally(() => { if (alive) setMatchLoading(false); });
    return () => { alive = false; };
  }, [selectedPb]);

  const adminName = (id: unknown): string | null => {
    const a = admins.find(x => x.id === Number(id));
    return a ? [a.vorname, a.nachname].filter(Boolean).join(' ') : null;
  };

  const filterBySearch = <T extends { name: string; id: number; plz?: string | null }>(list: T[], q: string): T[] => {
    const s = q.trim().toLowerCase();
    if (!s) return list;
    return list.filter(x => x.name.toLowerCase().includes(s) || String(x.id).includes(s) || (x.plz ?? '').includes(s));
  };

  const pbs = useMemo(() => filterBySearch(board?.pbs ?? [], pbSearch), [board, pbSearch]);

  // Rechte Spalte: ohne Auswahl alle verfügbaren BKs, mit Auswahl die
  // Kandidaten mit Score (Icons aus der Matching-Antwort) — beide nach
  // Startdatum sortiert (gleicher Tag → Score).
  const bkById = useMemo(() => new Map((board?.bks ?? []).map(b => [b.id, b])), [board]);
  type RightRow = MatchingBoardUser & {
    score: number | null; incomplete?: boolean;
    /** Feld-Breakdown inkl. Gewichten aus den Admin-Einstellungen. */
    fields?: MatchField[];
    /** Wohnraum-Treffer (bk_wohnen) für den gewählten PB — null ohne Auswahl. */
    wohnraumMatch?: boolean | null;
  };
  const rightRows: RightRow[] = useMemo(() => {
    if (selectedPb && candidates) {
      // Nur verfügbare BKs (Board-Filter) zeigen; Score aus dem Matching,
      // Kachel-Infos aus dem Board.
      return sortByAvailability(candidates
        .filter(c => bkById.has(c.id))
        .map(c => {
          const b = bkById.get(c.id)!;
          const wf = c.fields.find(f => f.name === 'bk_wohnen');
          return {
            ...b, icons: c.icons ?? b.icons, score: c.score, incomplete: c.incomplete, fields: c.fields,
            startdatum: c.startdatum !== undefined ? c.startdatum : b.startdatum,
            wohnraumMatch: wf ? wf.score >= 1 : null,
          };
        }));
    }
    return sortByAvailability((board?.bks ?? []).map(b => ({ ...b, score: null })));
  }, [selectedPb, candidates, board, bkById]);
  const visibleBks = useMemo(() => filterBySearch(rightRows, bkSearch), [rightRows, bkSearch]);

  // Aufschlüsselung schließen: bei geänderter BK-Suche und per Escape.
  useEffect(() => { setOpenBreakdown(null); }, [bkSearch]);
  useEffect(() => {
    if (openBreakdown == null) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpenBreakdown(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [openBreakdown]);

  const COL_HEADER: React.CSSProperties = {
    fontSize: 12, fontWeight: 700, color: t.heading, display: 'flex',
    alignItems: 'center', gap: 8, marginBottom: 10,
  };
  const SEARCH_STYLE: React.CSSProperties = {
    width: '100%', boxSizing: 'border-box', fontSize: 12.5,
    border: `1px solid ${t.border}`, borderRadius: 8, padding: '7px 10px',
    background: t.inputBg, color: t.text, outline: 'none', fontFamily: 'Outfit,sans-serif',
    marginBottom: 10,
  };

  // Karte eines Pflegeempfängers (in beiden PB-Spalten gleich).
  const renderPb = (pb: MatchingBoardUser) => {
    const selected = selectedPb?.id === pb.id;
    return (
      <Card
        key={pb.id}
        dark={dark}
        onClick={() => setSelectedPb(selected ? null : pb)}
        style={{
          padding: 14, cursor: 'pointer',
          border: `2px solid ${selected ? '#2ba3dd' : t.border}`,
          boxShadow: selected ? '0 0 0 3px rgba(43,163,221,0.15)' : undefined,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 14.5, fontWeight: 700, color: t.heading, display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <Avatar name={pb.name} userId={pb.id} size={28} dark={dark} />
            {pb.name}
            <span data-testid="matching-pb-kundennummer" style={{ fontSize: 12, fontWeight: 500, color: t.muted }}>
              {tr('matching.board.customer_no', { id: pb.id })}
            </span>
          </span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            {pb.pflegegrad != null && pb.pflegegrad !== '' && (
              <span style={{ fontSize: 13, fontWeight: 700, color: t.heading }}>{tr('matching.pg_badge', { value: String(pb.pflegegrad) })}</span>
            )}
            <AppLink
              page="pflege"
              sub={String(pb.id)}
              stopPropagation
              title={tr('matching.board.open_profile')}
              aria-label={tr('matching.board.open_profile_aria', { name: pb.name })}
              onClick={e => { if (pb.family_account_id == null) e.preventDefault(); }}
              style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 26, height: 26, border: `1px solid ${t.border}`, borderRadius: 7, background: t.inputBg, cursor: 'pointer', textDecoration: 'none' }}
            >
              <Icon.Edit c={t.muted} />
            </AppLink>
          </span>
        </div>
        <div style={{ marginTop: 8 }}>
          {/* Was die roten Wunsch-Icons zeigen, nicht doppelt in der Standardzeile. */}
          <CareParamIconRow
            keys={pb.icons.filter(k => !((WUNSCH_ICON_KEYS as readonly string[]).includes(k) && pb.wunsch_icons?.includes(k)))}
            dark={dark}
            size={18}
            gap={8}
          />
        </div>
        {/* Wunsch-Zeile (PDF): gewünschte BK-Kategorie (users.badge) */}
        <div style={{ marginTop: 8, paddingTop: 8, borderTop: `1px dashed ${t.borderSoft}`, fontSize: 12.5, color: t.text, display: 'flex', gap: 14, flexWrap: 'wrap' }}>
          <span data-testid="matching-pb-bk-category">{tr('matching.board.bk_category')}: <b>{badgeLabel(pb.badge) ?? '—'}</b></span>
          <WunschIcons keys={pb.wunsch_icons} />
        </div>
        <div style={{ marginTop: 8, fontSize: 12, color: t.muted, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <span data-testid="matching-pb-startdatum">{tr('matching.board.pb_startdatum')}: <b style={{ color: t.heading }}>{formatDate(pb.startdatum)}</b></span>
          {pb.ort && <span>{pb.ort}</span>}
          {adminName(pb.assigned_to) && <span>{tr('matching.board.responsible')}: {adminName(pb.assigned_to)}</span>}
        </div>
      </Card>
    );
  };

  const pbsNeu = pbs.filter(pb => !pb.bestandskunde);
  const pbsBestand = pbs.filter(pb => pb.bestandskunde);

  if (loading) {
    return <div style={{ padding: 28, fontSize: 13, color: t.muted }}>{tr('matching.board.loading')}</div>;
  }
  if (error) {
    return <div style={{ padding: 28, fontSize: 13, color: '#b42318' }}>{error}</div>;
  }

  return (
    <div style={{ padding: '24px 28px', fontFamily: 'Outfit, sans-serif', display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <SectionTitle
        dark={dark}
        title={tr('matching.board.title')}
        subtitle={tr('matching.board.subtitle', { stage: board?.last_phase?.title ?? tr('matching.board.default_stage') })}
      />

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1.2fr', gap: 16, marginTop: 16, flex: 1, minHeight: 0 }}>
        {/* ── Links: Pflegeempfänger, aufgeteilt in Neukunden (noch keine
            Betreuung) und Bestandskunden (hatten bereits eine Betreuung). */}
        {([
          { key: 'neu', title: tr('matching.board.pbs_new_title'), list: pbsNeu, color: '#2ba3dd' },
          { key: 'bestand', title: tr('matching.board.pbs_existing_title'), list: pbsBestand, color: '#12b76a' },
        ] as const).map(col => (
          <div key={col.key} data-pb-column={col.key} style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <div style={COL_HEADER}>
              <span style={{ width: 9, height: 9, borderRadius: '50%', background: col.color }} />
              {col.title}
              <Badge tone="neutral" dark={dark}>{col.list.length}</Badge>
            </div>
            {/* Suche gilt für beide PB-Spalten. */}
            <input value={pbSearch} onChange={e => setPbSearch(e.target.value)} placeholder={tr('matching.board.search_placeholder')} style={SEARCH_STYLE} />
            <div style={{ overflowY: 'auto', minHeight: 0, display: 'flex', flexDirection: 'column', gap: 10, paddingRight: 4 }}>
              {col.list.length === 0 && (
                <div style={{ fontSize: 12.5, color: t.muted, fontStyle: 'italic', padding: '10px 2px' }}>
                  {tr('matching.board.pbs_empty')}
                </div>
              )}
              {col.list.map(renderPb)}
            </div>
          </div>
        ))}

        {/* ── Rechts: Betreuungskräfte ───────────────────────────────── */}
        <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <div style={COL_HEADER}>
            <span style={{ width: 9, height: 9, borderRadius: '50%', background: '#e8792b' }} />
            {tr('matching.board.bks_title')}
            <Badge tone="neutral" dark={dark}>{visibleBks.length}</Badge>
            {selectedPb && (
              <span style={{ fontSize: 11.5, fontWeight: 500, color: t.muted }}>
                — {tr('matching.board.sorted_for')} <b style={{ color: BRAND }}>{selectedPb.name}</b> · {tr('matching.board.click_hint')}
              </span>
            )}
          </div>
          <input value={bkSearch} onChange={e => setBkSearch(e.target.value)} placeholder={tr('matching.board.search_placeholder')} style={SEARCH_STYLE} />
          <div style={{ overflowY: 'auto', minHeight: 0, display: 'flex', flexDirection: 'column', gap: 10, paddingRight: 4 }}>
            {matchLoading && (
              <div style={{ fontSize: 12.5, color: t.muted, padding: '10px 2px' }}>{tr('matching.list.loading')}</div>
            )}
            {!matchLoading && visibleBks.length === 0 && (
              <div style={{ fontSize: 12.5, color: t.muted, fontStyle: 'italic', padding: '10px 2px' }}>
                {tr('matching.board.bks_empty')}
              </div>
            )}
            {!matchLoading && visibleBks.map(bk => (
              <Card
                key={bk.id}
                dark={dark}
                onClick={selectedPb ? () => {
                  // BK fürs Matching gewählt → PB-Profil mit offenem
                  // "Neue Betreuung anlegen"-Drawer und dieser BK vorausgewählt.
                  setCareCreateHandoff({ pbUserId: selectedPb.id, bkUserId: bk.id });
                  if (selectedPb.family_account_id != null) {
                    navigateGlobal('pflege', String(selectedPb.id));
                  }
                } : undefined}
                style={{ padding: 14, border: `2px solid ${selectedPb ? '#e8792b55' : t.border}`, cursor: selectedPb ? 'pointer' : 'default' }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 14.5, fontWeight: 700, color: t.heading, display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    <Avatar name={bk.name} userId={bk.id} size={28} dark={dark} />
                    {bk.name}
                  </span>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    {busyBkIds.has(bk.id) && <Badge tone="warning" dark={dark}>{tr('matching.board.in_use')}</Badge>}
                    {bk.score != null && (
                      // Score — Klick öffnet die Aufschlüsselung mit den Gewichten
                      <span style={{ position: 'relative', display: 'inline-flex' }}>
                        <button
                          type="button"
                          data-testid={`matching-score-${bk.id}`}
                          title={tr('matching.breakdown.show')}
                          aria-expanded={openBreakdown === bk.id}
                          onClick={e => { e.stopPropagation(); setOpenBreakdown(openBreakdown === bk.id ? null : bk.id); }}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                        >
                          <MatchScoreBadge score={bk.score} incomplete={bk.incomplete} size="md" />
                        </button>
                        {openBreakdown === bk.id && (bk.fields?.length ?? 0) > 0 && (
                          <div
                            onClick={e => e.stopPropagation()}
                            style={{ position: 'absolute', top: '100%', right: 0, zIndex: 200, marginTop: 4, cursor: 'default' }}
                          >
                            <MatchDetailsPopover fields={bk.fields!} dark={dark} />
                          </div>
                        )}
                      </span>
                    )}
                    <AppLink
                      page="betreuung"
                      sub={String(bk.id)}
                      stopPropagation
                      title={tr('matching.board.open_profile')}
                      aria-label={tr('matching.board.open_profile_aria', { name: bk.name })}
                      style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 26, height: 26, border: `1px solid ${t.border}`, borderRadius: 7, background: t.inputBg, cursor: 'pointer', textDecoration: 'none' }}
                    >
                      <Icon.Edit c={t.muted} />
                    </AppLink>
                  </span>
                </div>
                {/* Info-Zeile: Startdatum, BK-Kategorie (users.badge), ggf. max. PG */}
                <div style={{ marginTop: 6, fontSize: 12.5, color: t.text, display: 'flex', gap: 14, flexWrap: 'wrap' }}>
                  <span data-startdatum={bk.startdatum ?? ''}>
                    {tr('matching.board.startdatum')}: <b>{formatStartdatum(bk.startdatum) ?? tr('matching.board.startdatum_now')}</b>
                  </span>
                  <span data-testid={`matching-bk-category-${bk.id}`}>{tr('matching.board.category_short')}: <b>{badgeLabel(bk.badge) ?? '—'}</b></span>
                  {bk.pflegegrad != null && bk.pflegegrad !== '' && <span>{tr('matching.pg_label')} <b>{String(bk.pflegegrad)}</b></span>}
                  <span>
                    {tr('matching.board.wohnraum_wanted')}: <b>{bk.wohnraum?.length ? bk.wohnraum.join(', ') : '—'}</b>
                    {/* Mit PB-Auswahl: passt der angebotene Wohnraum? */}
                    {selectedPb && bk.wohnraumMatch != null && (
                      <span
                        data-wohnraum-match={String(bk.wohnraumMatch)}
                        title={bk.wohnraumMatch ? tr('matching.board.wohnraum_match') : tr('matching.board.wohnraum_no_match')}
                        style={{ marginLeft: 6, fontWeight: 700, color: bk.wohnraumMatch ? '#12b76a' : '#b42318' }}
                      >{bk.wohnraumMatch ? '✓' : '✗'}</span>
                    )}
                  </span>
                </div>
                <div style={{ marginTop: 8 }}>
                  {/* Auswahl aktiv → übereinstimmende Pflegeparameter hervorheben */}
                  <CareParamIconRow
                    keys={bk.icons}
                    dark={dark}
                    size={18}
                    gap={8}
                    highlight={selectedPb ? (selectedPb.match_icons ?? selectedPb.icons) : undefined}
                  />
                </div>
                <div style={{ marginTop: 8, fontSize: 12, color: t.muted, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  {bk.ort && <span>{bk.ort}</span>}
                  {adminName(bk.assigned_to) && <span>{tr('matching.board.responsible')}: {adminName(bk.assigned_to)}</span>}
                </div>
              </Card>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
